import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloud, Loader2, Edit2, Trash2, X, Save, ImagePlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getGallery, updateGallery, deleteGallery, bulkUploadGallery } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';

const CATEGORIES = [
  { value: 'events', label: 'कार्यक्रम (Events)' },
  { value: 'readers', label: 'पाठक (Readers)' },
  { value: 'media', label: 'मीडिया (Media)' },
  { value: 'posters', label: 'पोस्टर (Posters)' },
];

const catLabel = (v) => (CATEGORIES.find((c) => c.value === v)?.label || v);

export default function AdminGallery() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadCategory, setUploadCategory] = useState('events');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', category: 'events', description: '' });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getGallery());
    } catch (e) {
      toast({ title: 'त्रुटि', description: 'फ़ोटो लोड नहीं हो सकीं', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      toast({ title: 'कोई छवि नहीं', description: 'कृपया केवल छवि फ़ाइलें चुनें', variant: 'destructive' });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const res = await bulkUploadGallery(files, uploadCategory, (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      });
      const createdCount = res.created?.length || 0;
      const skipped = res.skipped?.length || 0;
      toast({
        title: 'अपलोड सफल',
        description: `${createdCount} फ़ोटो जोड़ी गईं${skipped ? `, ${skipped} बहुत बड़ी (छोड़ी गईं)` : ''}`,
      });
      load();
    } catch (err) {
      toast({ title: 'अपलोड विफल', description: err.response?.data?.detail || 'पुनः प्रयास करें', variant: 'destructive' });
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const openEdit = (item) => {
    setEditing(item);
    setEditForm({ title: item.title || '', category: item.category || 'events', description: item.description || '' });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateGallery(editing.id, {
        title: editForm.title,
        image: editing.image,
        category: editForm.category,
        description: editForm.description,
      });
      toast({ title: 'सहेजा गया', description: 'फ़ोटो विवरण अपडेट हुआ' });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: 'त्रुटि', description: 'सहेजने में विफल', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('क्या आप इस फ़ोटो को हटाना चाहते हैं?')) return;
    try {
      await deleteGallery(item.id);
      toast({ title: 'हटाया गया' });
      load();
    } catch (err) {
      toast({ title: 'त्रुटि', description: 'हटाने में विफल', variant: 'destructive' });
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none';

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gallery / फ़ोटो गैलरी</h1>
          <p className="text-slate-500 text-sm mt-1">असीमित फ़ोटो अपलोड करें — शीर्षक, श्रेणी व विवरण संपादित करें</p>
        </div>
        <div className="text-sm text-slate-600 bg-white px-4 py-2 rounded-xl border border-slate-200">
          कुल फ़ोटो: <span className="font-bold text-orange-600">{items.length}</span>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
          <label className="text-sm font-medium text-slate-700">अपलोड श्रेणी:</label>
          <select
            value={uploadCategory}
            onChange={(e) => setUploadCategory(e.target.value)}
            data-testid="gallery-upload-category"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
          >
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="gallery-dropzone"
          className={`relative rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-300 p-10 text-center ${
            dragActive ? 'border-orange-500 bg-orange-50 scale-[1.01]' : 'border-slate-300 hover:border-orange-400 hover:bg-slate-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="gallery-file-input"
            onChange={(e) => e.target.files?.length && handleFiles(e.target.files)}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-10 w-10 text-orange-500 animate-spin" />
              <p className="text-slate-700 font-medium">अपलोड हो रहा है... {progress}%</p>
              <div className="w-full max-w-xs h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center">
                <UploadCloud className="h-8 w-8 text-orange-500" />
              </div>
              <div>
                <p className="text-slate-800 font-semibold text-lg">फ़ोटो यहाँ खींचें और छोड़ें</p>
                <p className="text-slate-500 text-sm mt-1">या क्लिक करके चुनें — एक साथ कई फ़ोटो चुन सकते हैं (अधिकतम 5MB प्रति फ़ोटो)</p>
              </div>
              <Button type="button" data-testid="gallery-add-photo-btn" className="bg-orange-500 hover:bg-orange-600 text-white mt-2">
                <ImagePlus className="h-4 w-4 mr-2" /> Add Photo / फ़ोटो जोड़ें
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-orange-500 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-400">अभी तक कोई फ़ोटो नहीं। ऊपर से अपलोड करें।</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden group" data-testid="gallery-photo-card">
              <div className="relative aspect-square overflow-hidden bg-slate-100">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <span className="absolute top-2 left-2 text-xs px-2 py-1 bg-black/60 text-white rounded-full">{catLabel(item.category).split(' ')[0]}</span>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-slate-900 text-sm truncate" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{item.title || 'शीर्षकहीन'}</h3>
                {item.description && <p className="text-xs text-slate-500 line-clamp-2 mt-1" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{item.description}</p>}
              </div>
              <div className="flex gap-2 p-3 pt-0">
                <button onClick={() => openEdit(item)} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-sm font-medium transition-colors" data-testid="gallery-edit-btn">
                  <Edit2 className="h-4 w-4" /> संपादित
                </button>
                <button onClick={() => handleDelete(item)} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors" data-testid="gallery-delete-btn">
                  <Trash2 className="h-4 w-4" /> हटाएं
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setEditing(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">फ़ोटो संपादित करें</h2>
                <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600"><X className="h-6 w-6" /></button>
              </div>
              <form onSubmit={saveEdit} className="p-6 space-y-5">
                <img src={editing.image} alt="" className="w-full h-48 object-cover rounded-xl" />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">शीर्षक (Title)</label>
                  <input value={editForm.title} onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))} className={inputCls} data-testid="gallery-edit-title" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">श्रेणी (Category)</label>
                  <select value={editForm.category} onChange={(e) => setEditForm((p) => ({ ...p, category: e.target.value }))} className={inputCls} data-testid="gallery-edit-category">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">विवरण (Description)</label>
                  <textarea rows={3} value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} className={inputCls} data-testid="gallery-edit-description" placeholder="इस फ़ोटो के बारे में..." />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={saving} data-testid="gallery-save-edit-btn" className="bg-orange-500 hover:bg-orange-600 text-white flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} सहेजें
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditing(null)}>रद्द करें</Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
